import React, { useId } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { AssetStyleId, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../components/primitives/Panel';
import { releaseThemeMessages } from '../themes/releaseThemeMessages';
import { assetStyleMessages, waveformShapeMessages } from '../themes/assetStyleMessages';
import { revisionMessages } from './revisionMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Revisions.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface NewRevisionFieldsProps {
  songThemeId: ReleaseThemeId;
  styleIds: readonly AssetStyleId[];
  waveforms: readonly WaveformShape[];
  style: AssetStyleId | undefined;
  waveform: WaveformShape | undefined;
  /** Null: the type has no text. */
  text: string | null;
  onStyle: (style: AssetStyleId) => void;
  onWaveform: (waveform: WaveformShape) => void;
  onText: (text: string) => void;
}

/**
 * The fields of "Make a new revision" (design d3): the theme of the song (read only: the theme
 * changes on the Overview tab), the style chips, the waveform chips, and the text when the type has one.
 */
const NewRevisionFieldsComponent: React.FC<NewRevisionFieldsProps> = ({
  songThemeId,
  styleIds,
  waveforms,
  style,
  waveform,
  text,
  onStyle,
  onWaveform,
  onText,
}) => {
  const intl = useIntl();
  const textId = useId();

  return (
    <>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{intl.formatMessage(revisionMessages.dialogTheme)}</span>
        <Panel nested pad>
          <b>{intl.formatMessage(releaseThemeMessages[songThemeId].name)}</b>{' '}
          <span className={styles.infoLine}>{intl.formatMessage(revisionMessages.dialogThemeNow)}</span>
        </Panel>
        <span className={styles.infoLine}>{intl.formatMessage(revisionMessages.dialogThemeNote)}</span>
      </div>
      {styleIds.length > 1 && (
        <div className={styles.field} role="group" aria-label={intl.formatMessage(revisionMessages.dialogStyle)}>
          <span className={styles.fieldLabel}>{intl.formatMessage(revisionMessages.dialogStyle)}</span>
          <div className={styles.chips}>
            {styleIds.map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={one === style}
                className={clsx(styles.chip, one === style && styles.chipOn)}
                onClick={() => onStyle(one)}
              >
                {intl.formatMessage(assetStyleMessages[one].name)}
              </button>
            ))}
          </div>
        </div>
      )}
      {waveforms.length > 0 && (
        <div className={styles.field} role="group" aria-label={intl.formatMessage(revisionMessages.dialogWaveform)}>
          <span className={styles.fieldLabel}>{intl.formatMessage(revisionMessages.dialogWaveform)}</span>
          <div className={styles.chips}>
            {waveforms.map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={one === waveform}
                className={clsx(styles.chip, one === waveform && styles.chipOn)}
                onClick={() => onWaveform(one)}
              >
                {intl.formatMessage(waveformShapeMessages[one])}
              </button>
            ))}
          </div>
        </div>
      )}
      {text !== null && (
        <label className={styles.field} htmlFor={textId}>
          <span className={styles.fieldLabel}>{intl.formatMessage(revisionMessages.dialogText)}</span>
          <input
            id={textId}
            className={styles.textInput}
            value={text}
            maxLength={200}
            onChange={(event) => onText(event.target.value)}
          />
        </label>
      )}
    </>
  );
};

export const NewRevisionFields = TypedMemo(NewRevisionFieldsComponent);
